import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import LanguageSwitcher from "../components/LanguageSwitcher";
import {
  Beaker, FlaskConical, MessageSquare, FileText, Database, LogOut,
  ArrowRight, Sparkles, Plus, ChevronRight, Brain, Clock, Shield
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ chats: 0, formulations: 0, knowledge: 0, documents: 0, training: 0 });
  const [chats, setChats] = useState([]);
  const [forms, setForms] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/dashboard/stats`).then((r) => setStats(r.data)).catch(() => {}),
      axios.get(`${API}/chat-history`).then((r) => setChats(r.data.slice(0, 4))).catch(() => {}),
      axios.get(`${API}/formulation-history`).then((r) => setForms(r.data.slice(0, 4))).catch(() => {}),
    ]);
  }, []);

  const quickActions = [
    { icon: MessageSquare, title: t("dashboard.askElastoAI"), desc: t("dashboard.askElastoAIDesc"), path: "/chat", color: "bg-blue-500" },
    { icon: FlaskConical, title: t("dashboard.predictFormulation"), desc: t("dashboard.predictFormulationDesc"), path: "/formulation", color: "bg-orange-500" },
    { icon: FlaskConical, title: t("dashboard.forwardPrediction"), desc: t("dashboard.forwardPredictionDesc"), path: "/forward-prediction", color: "bg-amber-500" },
    { icon: FileText, title: t("dashboard.documents"), desc: t("dashboard.documentsDesc"), path: "/documents", color: "bg-green-500" },
    { icon: Brain, title: t("dashboard.trainWithData"), desc: t("dashboard.trainWithDataDesc"), path: "/training", color: "bg-pink-500" },
    ...(user?.is_admin ? [{ icon: Database, title: t("dashboard.knowledgeBase"), desc: t("dashboard.knowledgeBaseDesc"), path: "/knowledge", color: "bg-purple-500" }] : []),
  ];

  const navItems = [
    { icon: MessageSquare, label: t("nav.chat"), path: "/chat" },
    { icon: FlaskConical, label: t("nav.formulation"), path: "/formulation" },
    { icon: FlaskConical, label: t("nav.forwardPrediction"), path: "/forward-prediction" },
    { icon: FileText, label: t("nav.documents"), path: "/documents" },
    { icon: Brain, label: t("nav.training"), path: "/training" },
    ...(user?.is_admin ? [{ icon: Database, label: t("nav.knowledge"), path: "/knowledge" }] : []),
    ...(user?.is_admin ? [{ icon: Shield, label: t("nav.admin"), path: "/admin" }] : []),
  ];

  return (
    <div className="flex h-screen bg-slate-50">
      <div className="w-72 bg-white border-r border-slate-200 flex flex-col">
        <div className="p-4 border-b border-slate-200">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-orange-500 flex items-center justify-center"><Beaker className="w-5 h-5 text-white" /></div>
            <span className="font-heading font-bold text-lg text-slate-900">ElastoAI</span>
          </Link>
        </div>
        <div className="p-4">
          <Button onClick={() => navigate("/chat")} data-testid="new-chat-btn" className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full">
            <Plus className="w-4 h-4 mr-2" /> New Chat
          </Button>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((item) => (
            <Link key={item.path} to={item.path} data-testid={`nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-600 hover:bg-slate-50 hover:text-slate-900">
              <item.icon className="w-5 h-5" /><span className="font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-200 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center">
            <span className="text-sm font-medium text-slate-600">{user?.name?.charAt(0).toUpperCase()}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          </div>
          <button onClick={logout} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="logout-btn">
            <LogOut className="w-4 h-4 text-slate-500" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="p-8 max-w-6xl mx-auto">
          <div className="mb-8 flex items-start justify-between gap-4">
            <div>
              <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">{t("dashboard.welcome")}, {user?.name?.split(" ")[0]}</h1>
              <p className="text-slate-600">Polymer & elastomer intelligence — what shall we work on?</p>
            </div>
            <LanguageSwitcher className="bg-white rounded-full border border-slate-200 px-3 py-1.5 shadow-sm" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {quickActions.map((a, i) => (
              <Link key={i} to={a.path} data-testid={`quick-action-${a.path.slice(1)}`}
                className="bg-white rounded-xl border border-slate-200 p-5 hover:border-orange-300 hover:shadow-md hover:-translate-y-0.5 transition-all group">
                <div className={`w-10 h-10 rounded-lg ${a.color} flex items-center justify-center mb-3`}><a.icon className="w-5 h-5 text-white" /></div>
                <h3 className="font-heading font-semibold text-slate-900 mb-1 group-hover:text-orange-600">{a.title}</h3>
                <p className="text-sm text-slate-500">{a.desc}</p>
              </Link>
            ))}
          </div>

          <div className={`grid grid-cols-2 ${user?.is_admin ? 'md:grid-cols-5' : 'md:grid-cols-4'} gap-4 mb-8`}>
            {[
              ["Chats", stats.chats],
              ["Formulations", stats.formulations],
              ...(user?.is_admin ? [["Knowledge", stats.knowledge]] : []),
              ["Documents", stats.documents],
              ["Training", stats.training],
            ].map(([lbl, v]) => (
              <div key={lbl} className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wider">{lbl}</p>
                <p className="font-heading text-2xl font-bold text-slate-900">{v}</p>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><Clock className="w-4 h-4 text-slate-400" /> Recent Chats</h2>
                <Link to="/chat" className="text-sm text-orange-500 flex items-center gap-1">View all <ChevronRight className="w-4 h-4" /></Link>
              </div>
              {chats.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">No conversations yet</p>
              ) : chats.map((c) => (
                <Link key={c.session_id} to={`/chat/${c.session_id}`} className="block p-3 rounded-lg hover:bg-slate-50">
                  <p className="text-sm font-medium text-slate-900 truncate">{c.title}</p>
                  <p className="text-xs text-slate-500">{new Date(c.updated_at).toLocaleDateString()}</p>
                </Link>
              ))}
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><Sparkles className="w-4 h-4 text-slate-400" /> Recent Formulations</h2>
                <Link to="/formulation" className="text-sm text-orange-500 flex items-center gap-1">New <ChevronRight className="w-4 h-4" /></Link>
              </div>
              {forms.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">No formulations yet</p>
              ) : forms.map((f) => (
                <Link key={f.id} to={`/formulation/${f.id}`} className="block p-3 rounded-lg bg-slate-50 mb-2 hover:bg-slate-100">
                  <p className="text-sm font-medium text-slate-900">{f.request?.elastomer_type || "Custom"} — {f.request?.application || "General"}</p>
                  <p className="text-xs text-slate-500">{f.request?.hardness_shore_a && `${f.request.hardness_shore_a} Shore A • `}{new Date(f.created_at).toLocaleDateString()}</p>
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-8 bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-6 text-white flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-orange-500 flex items-center justify-center flex-shrink-0"><Sparkles className="w-6 h-6 text-white" /></div>
            <div>
              <h3 className="font-heading font-semibold text-lg mb-1">Pro tip</h3>
              <p className="text-slate-300 text-sm">Upload your technical datasheets in <Link to="/training" className="underline">Training</Link> — they get indexed into the RAG and the chat will cite them as ground-truth.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
